<template>
  <div class="survey-responses-container">
    <div class="glass-header mb-4">
      <button class="btn btn-light mb-3 text-muted fw-medium border-0 shadow-sm" @click="$router.push('/survey-management')">
        <i class="fa fa-arrow-left me-2"></i> Back to Surveys
      </button>
      <div v-if="survey">
        <h2 class="mb-0 fw-bold text-dark">{{ survey.title }} - Responses</h2>
        <p class="text-muted mb-0">Analytics and individual submissions</p>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
    </div>
    
    <div v-else-if="!survey" class="text-center py-5 text-muted">
      <h4>Survey not found.</h4>
    </div>

    <div v-else>
      <!-- Quick Stats -->
      <div class="row mb-5">
        <div class="col-md-4">
          <div class="kpi-card glass-panel text-center p-4 d-flex flex-column justify-content-center h-100">
            <h1 class="display-3 fw-bold text-primary mb-2" style="color: #4f46e5 !important;">{{ filteredResponses.length }}</h1>
            <p class="text-muted text-uppercase fw-bold mb-0" style="letter-spacing: 1px;">Total Responses</p>
          </div>
        </div>
        <div class="col-md-8">
           <div class="glass-panel p-4 h-100 d-flex flex-column justify-content-center">
             <h5 class="fw-bold mb-3 text-dark">Response Activity</h5>
             <div class="text-muted">
               <p v-if="filteredResponses.length === 0" class="mb-0">No responses match the current selection.</p>
               <p v-else class="mb-0 fs-5">Latest response received on: <span class="fw-bold text-dark">{{ new Date(filteredResponses[filteredResponses.length-1].attributes.createdAt).toLocaleString() }}</span></p>
             </div>
           </div>
        </div>
      </div>

      <!-- Filter Bar -->
      <div v-if="busNumberQuestionId && uniqueBuses.length > 0" class="glass-panel p-4 mb-5 d-flex align-items-center shadow-sm border-0 rounded-4">
        <h5 class="fw-bold mb-0 me-4 text-dark"><i class="fa fa-filter text-primary me-2"></i> Filter by Bus Number:</h5>
        <select class="form-select form-select-lg bg-light border-0 shadow-sm" style="max-width: 300px;" v-model="selectedBusFilter">
          <option value="">All Buses</option>
          <option v-for="bus in uniqueBuses" :key="bus" :value="bus">Bus {{ bus }}</option>
        </select>
      </div>

      <!-- Analytics Graphs -->
      <div v-if="computedAnalytics.length > 0" class="row mb-5">
        <div class="col-12 mb-4">
          <h4 class="fw-bold text-dark">Visual Analytics</h4>
          <p class="text-muted mb-0">Aggregated feedback from structured questions</p>
        </div>
        <div v-for="(analytic, idx) in computedAnalytics" :key="idx" class="col-md-6 mb-4">
          <div class="glass-panel p-4 h-100 border-0 shadow-sm">
            <h5 class="fw-bold mb-1 text-dark">{{ analytic.questionText }}</h5>
            <p class="text-muted small mb-4 text-uppercase fw-medium">{{ analytic.type }} • {{ analytic.totalAnswers }} answers</p>
            
            <div class="analytics-bars">
              <div v-for="(res, rIdx) in analytic.results" :key="rIdx" class="mb-3">
                <div class="d-flex justify-content-between mb-2 small">
                  <span class="fw-bold text-secondary">{{ res.label }}</span>
                  <span class="text-primary fw-bold">{{ res.percentage }}% <span class="text-muted fw-normal">({{ res.count }})</span></span>
                </div>
                <div class="progress shadow-sm" style="height: 10px; border-radius: 6px; background-color: #F0EDFF;">
                  <div class="progress-bar" role="progressbar" :style="{ width: res.percentage + '%', backgroundColor: '#6C5DD3', borderRadius: '6px' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detailed Responses Table -->
      <div class="glass-panel p-4 border-0 shadow-sm rounded-4">
        <h4 class="fw-bold mb-4 text-dark">Individual Submissions</h4>
        
        <div v-if="responses.length === 0" class="text-center text-muted py-5">
          <i class="fa fa-folder-open fs-1 mb-3 opacity-50"></i>
          <p class="fs-5">No submissions available yet.</p>
        </div>
        
        <div class="table-responsive" v-else>
          <table class="table table-hover align-middle custom-table">
            <thead class="table-light">
              <tr>
                <th class="ps-3 cursor-pointer" @click="toggleSort('id')">ID <i v-if="sortKey==='id'" :class="sortDesc ? 'fa fa-sort-down' : 'fa fa-sort-up'"></i></th>
                <th>Submitted By (Host ID)</th>
                <th v-if="busNumberQuestionId" class="cursor-pointer" @click="toggleSort('busNumber')">Bus Number <i v-if="sortKey==='busNumber'" :class="sortDesc ? 'fa fa-sort-down' : 'fa fa-sort-up'"></i></th>
                <th class="cursor-pointer" @click="toggleSort('date')">Date <i v-if="sortKey==='date'" :class="sortDesc ? 'fa fa-sort-down' : 'fa fa-sort-up'"></i></th>
                <th class="text-end pe-3">View Details</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="res in sortedResponses" :key="res.id">
                <td class="fw-bold text-muted ps-3">#{{ res.id }}</td>
                <td class="fw-medium text-dark">{{ res.attributes.user?.data?.id || 'Anonymous' }}</td>
                <td v-if="busNumberQuestionId" class="text-dark fw-medium">{{ res.attributes.answers && res.attributes.answers[busNumberQuestionId] ? res.attributes.answers[busNumberQuestionId] : 'N/A' }}</td>
                <td class="text-muted">{{ new Date(res.attributes.createdAt).toLocaleString() }}</td>
                <td class="text-end pe-3">
                  <button class="btn btn-sm btn-outline-primary rounded-pill px-4 fw-medium" @click="viewDetails(res)">
                    View Answers
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Details Modal (Custom Implementation) -->
      <div v-if="selectedResponse" class="modal-overlay" @click.self="selectedResponse = null">
        <div class="modal-card glass-panel p-4 border-0 shadow-lg">
          <div class="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
            <div>
              <h4 class="fw-bold mb-0 text-dark">Response #{{ selectedResponse.id }}</h4>
              <small class="text-muted">Submitted on {{ new Date(selectedResponse.attributes.createdAt).toLocaleString() }}</small>
            </div>
            <button class="btn-close fs-5" @click="selectedResponse = null"></button>
          </div>
          <div class="answers-list pe-2" style="max-height: 65vh; overflow-y: auto;">
            <div v-for="(q, index) in survey.questions" :key="q.id" class="mb-4">
              <h6 class="fw-bold text-dark fs-6">{{ index + 1 }}. {{ q.question_text }}</h6>
              <div class="p-3 rounded text-dark fw-medium border" style="background-color: #f8fafc;">
                {{ formatAnswer(selectedResponse.attributes.answers[q.id]) }}
              </div>
            </div>
          </div>
          <div class="text-end mt-4 pt-3 border-top">
            <button class="btn btn-primary-custom rounded-pill px-5 fw-bold" @click="selectedResponse = null">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SurveyResponses',
  data() {
    return {
      survey: null,
      responses: [],
      loading: true,
      selectedResponse: null,
      sortKey: 'date',
      sortDesc: true,
      selectedBusFilter: ''
    };
  },
  computed: {
    busNumberQuestionId() {
      if (!this.survey || !this.survey.questions) return null;
      const q = this.survey.questions.find(q => q.question_text.toLowerCase().includes('bus'));
      return q ? q.id : null;
    },
    uniqueBuses() {
      if (!this.busNumberQuestionId || !this.responses.length) return [];
      const buses = new Set();
      this.responses.forEach(res => {
        const val = res.attributes.answers ? res.attributes.answers[this.busNumberQuestionId] : null;
        if (val !== null && val !== undefined && val !== '') buses.add(String(val).trim());
      });
      return Array.from(buses).sort((a, b) => parseInt(a) - parseInt(b));
    },
    filteredResponses() {
      if (!this.selectedBusFilter || !this.busNumberQuestionId) return [...this.responses];
      return this.responses.filter(res => {
        const val = res.attributes.answers ? res.attributes.answers[this.busNumberQuestionId] : null;
        return String(val).trim() === String(this.selectedBusFilter).trim();
      });
    },
    sortedResponses() {
      let sorted = [...this.filteredResponses];
      if (this.sortKey === 'busNumber' && this.busNumberQuestionId) {
        sorted.sort((a, b) => {
          const valA = a.attributes.answers ? a.attributes.answers[this.busNumberQuestionId] : null;
          const valB = b.attributes.answers ? b.attributes.answers[this.busNumberQuestionId] : null;
          const numA = parseInt(valA) || 0;
          const numB = parseInt(valB) || 0;
          return this.sortDesc ? numB - numA : numA - numB;
        });
      } else if (this.sortKey === 'date') {
        sorted.sort((a, b) => {
          const dateA = new Date(a.attributes.createdAt);
          const dateB = new Date(b.attributes.createdAt);
          return this.sortDesc ? dateB - dateA : dateA - dateB;
        });
      } else if (this.sortKey === 'id') {
        sorted.sort((a, b) => {
          return this.sortDesc ? b.id - a.id : a.id - b.id;
        });
      }
      return sorted;
    },
    computedAnalytics() {
      if (!this.survey || !this.filteredResponses.length) return [];
      
      const analytics = [];
      
      this.survey.questions.forEach(q => {
        if (['Single Select', 'Dropdown', 'Rating Scale', 'Multi Select'].includes(q.question_type)) {
          const stats = {};
          
          // Pre-populate stats to ensure all options appear even if 0 votes
          if (q.options && q.options.length) {
            q.options.forEach(opt => stats[opt] = 0);
          } else if (q.question_type === 'Rating Scale') {
            ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'].forEach(r => stats[r] = 0);
          }

          let totalAnswers = 0;
          this.filteredResponses.forEach(res => {
             let ans = res.attributes.answers ? res.attributes.answers[q.id] : null;
             if (ans !== null && ans !== undefined && ans !== '') {
               if (q.question_type === 'Multi Select' && Array.isArray(ans)) {
                 ans.forEach(a => {
                    if (stats[a] !== undefined) stats[a]++;
                    else stats[a] = 1;
                    totalAnswers++; 
                 });
               } else {
                 if (q.question_type === 'Rating Scale') ans = `${ans} Star${ans > 1 ? 's' : ''}`;
                 if (stats[ans] !== undefined) stats[ans]++;
                 else stats[ans] = 1;
                 totalAnswers++;
               }
             }
          });
          
          // For multi-select, percentage is out of total responses, not total answers (since 1 person can pick many)
          const baseTotal = q.question_type === 'Multi Select' ? this.filteredResponses.length : totalAnswers;

          const results = Object.keys(stats).map(key => {
            const count = stats[key];
            const percentage = baseTotal > 0 ? Math.round((count / baseTotal) * 100) : 0;
            return { label: key, count, percentage };
          });
          
          // Sort results: highest percentage first, except for Rating Scale (sort naturally 1 to 5)
          if (q.question_type !== 'Rating Scale') {
             results.sort((a, b) => b.count - a.count);
          }
          
          analytics.push({
             questionText: q.question_text,
             type: q.question_type,
             totalAnswers: q.question_type === 'Multi Select' ? this.filteredResponses.length : totalAnswers,
             results
          });
        }
      });
      return analytics;
    }
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        const id = this.$route.params.id;
        const token = localStorage.getItem('jwt');
        const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';
        
        // Fetch survey schema
        const surveyRes = await axios.get(`${baseUrl}/api/surveys/${id}?populate=questions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.survey = { id: surveyRes.data.data.id, ...surveyRes.data.data.attributes };

        // Fetch responses
        const resRes = await axios.get(`${baseUrl}/api/survey-responses?filters[survey][id][$eq]=${id}&populate=user`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.responses = resRes.data.data;
      } catch (e) {
        console.error("Failed to load responses", e);
      } finally {
        this.loading = false;
      }
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortKey = key;
        this.sortDesc = true;
      }
    },
    viewDetails(res) {
      this.selectedResponse = res;
    },
    formatAnswer(answer) {
      if (answer === null || answer === undefined || answer === '') return 'No Answer Provided';
      if (Array.isArray(answer)) return answer.join(', ');
      return String(answer);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

.survey-responses-container {
  font-family: 'Outfit', sans-serif;
  max-width: 1300px;
  margin: 0 auto;
  padding: 2rem;
  background-color: #F8F9FC;
  min-height: 100vh;
}

.text-gradient {
  background: linear-gradient(135deg, #6C5DD3, #8a7df0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.text-primary { color: #6C5DD3 !important; }

.btn-primary-custom {
  background-color: #6C5DD3;
  border-color: #6C5DD3;
  color: white;
}
.btn-primary-custom:hover {
  background-color: #5b4eb8;
  color: white;
}

.btn-outline-primary {
  color: #6C5DD3;
  border-color: #6C5DD3;
}
.btn-outline-primary:hover {
  background-color: #6C5DD3;
  color: white;
}

.glass-panel {
  background: #ffffff;
  border: 1px solid #E2E2EA;
  border-radius: 12px;
}

.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(17, 20, 45, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-card {
  width: 90%;
  max-width: 700px;
  background: white;
  border-radius: 20px;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.custom-table th {
  text-transform: uppercase;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #B2B2BF;
  border-bottom: 1px solid #E2E2EA;
  padding: 1rem 0.5rem;
  background-color: transparent !important;
}
.cursor-pointer {
  cursor: pointer;
  user-select: none;
}
.cursor-pointer:hover {
  color: #11142D;
}
.custom-table td {
  padding: 1.2rem 0.5rem;
  border-bottom: 1px solid #E2E2EA;
}
.custom-table tr:last-child td {
  border-bottom: none;
}

/* Custom Scrollbar for Modal */
.answers-list::-webkit-scrollbar {
  width: 6px;
}
.answers-list::-webkit-scrollbar-track {
  background: #f1f1f1; 
  border-radius: 10px;
}
.answers-list::-webkit-scrollbar-thumb {
  background: #c1c1c1; 
  border-radius: 10px;
}
.answers-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8; 
}
</style>
